// ============================================================
//  StoreDelivery.js — delivery areas & fees, pickup, free delivery
//  Customers pick their area from a WhatsApp list at checkout (max 9 areas
//  + pickup, which is what one WhatsApp list can show).
// ============================================================
import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { storeAction } from '../../api/client';
import { naira, Switch } from './storeUi';

const MAX_ZONES = 9;

export default function StoreDelivery({ bId, store, zones, onSaved }) {
  const [rows, setRows] = useState(zones.length ? zones.map(z => ({ ...z, fee: String(z.fee), eta: z.eta || '' })) : [{ name: '', fee: '', eta: '' }]);
  const [opt, setOpt] = useState({ pickup: store.pickup, pickupAddress: store.pickupAddress || '', freeOver: store.freeOver ? String(store.freeOver) : '', askUnlisted: store.askUnlisted });
  const [busy, setBusy] = useState(false);
  const setRow = (i, k, v) => setRows(r => r.map((x, n) => (n === i ? { ...x, [k]: v } : x)));

  async function save() {
    const list = rows.filter(r => r.name.trim()).map(r => ({ name: r.name.trim(), fee: Number(String(r.fee).replace(/[^\d.]/g, '')) || 0, eta: r.eta.trim() }));
    if (!list.length && !opt.pickup) { toast.error('Add at least one delivery area, or turn on pickup'); return; }
    if (opt.pickup && !opt.pickupAddress.trim()) { toast.error('Add the pickup address'); return; }
    setBusy(true);
    try {
      const z = await storeAction('zones', bId, { zones: list });
      const s = await storeAction('settings', bId, { pickup: opt.pickup, pickupAddress: opt.pickupAddress, freeOver: opt.freeOver === '' ? null : Number(opt.freeOver), askUnlisted: opt.askUnlisted });
      onSaved({ zones: z.data.zones, store: s.data.store });
      toast.success('Delivery saved');
    } catch (e) { toast.error(e?.response?.data?.message || 'Could not save'); }
    setBusy(false);
  }

  const preview = rows.filter(r => r.name.trim());
  return (
    <div className="stp-split">
      <div className="stp-col">
        <section className="stp-card" aria-labelledby="stp-zones">
          <div className="stp-card-head"><h2 id="stp-zones">Delivery areas &amp; fees</h2>
            {rows.length < MAX_ZONES && <button type="button" className="stp-btn stp-btn-ghost stp-btn-sm" onClick={() => setRows(r => [...r, { name: '', fee: '', eta: '' }])}><i className="fa-solid fa-plus" /> Add area</button>}
          </div>
          <div className="stp-zone-head" aria-hidden="true"><span>Area customers pick</span><span>Fee (₦)</span><span>Arrives in</span><span /></div>
          {rows.map((r, i) => (
            <div key={i} className="stp-zone">
              <input aria-label="Area name" value={r.name} maxLength={60} onChange={e => setRow(i, 'name', e.target.value)} placeholder="e.g. Lekki Phase 1" />
              <input aria-label="Fee" inputMode="decimal" value={r.fee} onChange={e => setRow(i, 'fee', e.target.value)} placeholder="1500" />
              <input aria-label="Delivery time" value={r.eta} maxLength={30} onChange={e => setRow(i, 'eta', e.target.value)} placeholder="35–50 min" />
              <button type="button" className="stp-icon" aria-label={`Remove ${r.name || 'area'}`} onClick={() => setRows(x => x.filter((_, n) => n !== i))}><i className="fa-regular fa-trash-can" /></button>
            </div>
          ))}
        </section>
        <section className="stp-card" aria-labelledby="stp-dopts">
          <h2 id="stp-dopts">Other options</h2>
          <div className="stp-row"><span><b>Pickup</b><small>Customers collect from you — free</small></span><Switch on={opt.pickup} onChange={v => setOpt(o => ({ ...o, pickup: v }))} label="Pickup" /></div>
          {opt.pickup && <label className="stp-field">Pickup address<input value={opt.pickupAddress} maxLength={200} onChange={e => setOpt(o => ({ ...o, pickupAddress: e.target.value }))} placeholder="5 Fola Osibo St, Lekki" /></label>}
          <label className="stp-field">Free delivery on orders above (₦) <small>· leave empty for never</small>
            <input inputMode="decimal" value={opt.freeOver} onChange={e => setOpt(o => ({ ...o, freeOver: e.target.value.replace(/[^\d.]/g, '') }))} placeholder="25000" /></label>
          <div className="stp-row"><span><b>“My area isn’t listed”</b><small>Let them order anyway; you agree the fee in chat</small></span><Switch on={opt.askUnlisted} onChange={v => setOpt(o => ({ ...o, askUnlisted: v }))} label="Allow areas not listed" /></div>
        </section>
        <button type="button" className="stp-btn stp-btn-dark stp-self-end" onClick={save} disabled={busy}>{busy ? <span className="stp-spin" /> : null}Save delivery</button>
      </div>

      <aside className="stp-pe-preview" aria-label="WhatsApp preview">
        <span className="stp-pe-k">Live preview · WhatsApp</span>
        <div className="stp-wa">
          <div className="stp-wa-bubble">Where should we deliver?</div>
          <div className="stp-wa-list">
            <b>DELIVERY</b>
            {preview.map((z, i) => <div key={i} className="stp-wa-li"><span><b>{z.name}</b><small>{naira(Number(String(z.fee).replace(/[^\d.]/g, '')) || 0)}{z.eta ? ` · ${z.eta}` : ''}</small></span><i /></div>)}
            {opt.pickup && <div className="stp-wa-li"><span><b>Pickup</b><small>Free{opt.pickupAddress ? ` · ${opt.pickupAddress}` : ''}</small></span><i /></div>}
            {opt.askUnlisted && preview.length > 0 && <div className="stp-wa-li"><span><b>My area isn’t listed</b><small>We’ll confirm the fee with you</small></span><i /></div>}
            {!preview.length && !opt.pickup && <div className="stp-wa-li"><span><small>Add an area to see it here</small></span></div>}
          </div>
        </div>
      </aside>
    </div>
  );
}
